import {
  ButtonBase,
  Stack,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { KeycloakIdentity } from "../../providers/authProvider";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import { PushPin } from "@mui/icons-material";
import {
  useGetIdentity,
  useLogout,
  useTranslation,
  useNavigation,
  useSetLocale,
  useGetLocale,
  useInvalidate,
} from "@refinedev/core";
import { ColorModeContext } from "../../providers/colorProvider";
import { useWorkspaceStore } from "../../store/useWorkspaceStore";
import { CurrencyToggle } from "./CurrencyToggle";
import { SelectStore } from "../../pages/stores/select-stores/SelectStore";
import { useMarketplaceStore } from "../../store/useMarketplaceStore";
import { useCurrency } from "../../hooks/useCurrency";
import { setAxiosLanguage } from "../../utils/axios";

const ghostSelectSx = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": { py: 0.25, pl: 0.5 },
};

export const UserMenu = ({
  showMarketplace = true,
}: {
  showMarketplace?: boolean;
}) => {
  const { data: user } = useGetIdentity<KeycloakIdentity>();
  const { translate } = useTranslation();
  const { push } = useNavigation();
  const { mode, toggleMode } = useContext(ColorModeContext);
  const { toggle: toggleWorkspace, items: workspaceItems } =
    useWorkspaceStore();

  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const currentLocale = locale();
  const invalidate = useInvalidate();

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorElement);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleChangeLanguage = (lang: string) => {
    setAxiosLanguage(lang);
    changeLanguage(lang);
    invalidate({ resource: "all", invalidates: ["all"] });
  };

  const nextLocale = currentLocale === "tr" ? "en" : "tr";

  const { mutate: logout } = useLogout();

  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);
  const { isLoading: isCurrencyLoading, settings: currencySettings } =
    useCurrency();
  const showCurrencyToggle = Boolean(
    selectedCountry &&
      selectedCountry.currency &&
      selectedCountry.currency !== "$" &&
      !isCurrencyLoading &&
      currencySettings,
  );

  if (!user) {
    return null;
  }

  return (
    <>
      <ButtonBase
        TouchRippleProps={{ style: { borderRadius: "8px", margin: "-4px" } }}
        onClick={handleOpen}
      >
        <Stack direction="row" gap="8px" alignItems="center">
          <Avatar
            alt={user.name + " " + user.surname}
            sx={{ width: 32, height: 32, fontSize: 16 }}
            variant="rounded"
          >
            {user.name[0] + user.surname[0]}
          </Avatar>
          <Typography
            variant="subtitle2"
            sx={{ display: { xs: "none", sm: "inline-block" } }}
          >
            {translate("pages.layout.welcomeMessage", { name: user.name })}
          </Typography>
          <KeyboardArrowDownIcon />
        </Stack>
      </ButtonBase>

      <Menu
        anchorEl={anchorElement}
        id="account-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        MenuListProps={{ dense: true }}
        sx={{
          mt: 1,
          "& .MuiPaper-root": { minWidth: 220 },
        }}
      >
        {showMarketplace && (
          <MenuItem
            disableRipple
            onClick={(e) => e.stopPropagation()}
            sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              Mağaza
            </Typography>
            <SelectStore compact disablePortal sx={ghostSelectSx} />
          </MenuItem>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
            py: 0.75,
            mx: 1,
            my: 0.5,
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          {showCurrencyToggle && <CurrencyToggle />}

          <Tooltip title={mode === "dark" ? "Açık tema" : "Koyu tema"}>
            <IconButton
              onClick={toggleMode}
              color="inherit"
              sx={{ width: 34, height: 34 }}
            >
              {mode === "dark" ? (
                <LightModeOutlined fontSize="small" />
              ) : (
                <DarkModeOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Workspace">
            <IconButton
              color="inherit"
              onClick={() => {
                toggleWorkspace();
                handleClose();
              }}
              sx={{ width: 34, height: 34, position: "relative" }}
            >
              <PushPin fontSize="small" />
              {workspaceItems.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip
            title={nextLocale === "en" ? "Switch to English" : "Türkçe'ye geç"}
          >
            <IconButton
              color="inherit"
              onClick={() => handleChangeLanguage(nextLocale)}
              sx={{ width: 34, height: 34 }}
            >
              <Typography fontSize="1rem" fontWeight={700} lineHeight={1}>
                {(currentLocale ?? "tr").toUpperCase()}
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>

        <Divider />

        <MenuItem
          onClick={() => {
            push("extension");
            handleClose();
          }}
        >
          {translate("pages.layout.extension")}
        </MenuItem>

        <MenuItem
          onClick={() => {
            logout();
            handleClose();
          }}
        >
          {translate("pages.layout.logout")}
        </MenuItem>
      </Menu>
    </>
  );
};
